import React, { useEffect, useState, useRef, useCallback } from "react";
import { useOutletContext, useSearchParams, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { produce } from "immer";
import { filterActions, getFilterState } from "@/entities/filter";
import styles from "./ImageSearch.module.scss";

import { SEARCH_MODE_OCR } from "@/Views/constants";
import { useCurrentOrganization, useMe, useAPI } from "@/Hooks";
import { toast } from "react-toastify";
import { RightClickModal } from "@/shared/ui/RightClickModal/RightClickModal";
import { handleOpenVideoPage } from "@/app/libs/const/handleOpenVideoPage";
import { NoContextItems } from "../SearchView2/NoContext/NoContextItems";
import { useSelectedScreens } from "@/entities/selectedScreens";
import SelectedScreensPopup from "../Virtual/SelectedScreensPopup/SelectedScreensPopup";
import { loadingStatesActions } from "@/entities/loadingStates";
import SimilarScreenModal from "../SimilarScreen/SimilarScreenModal/SimilarScreenModal";

const ImageSearch = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { isActiveMagic } = useSelector(getFilterState);
  const api = useAPI();
  const { filterParams, prevFilters, setSearchValue, handleOpenBillingPage } =
    useOutletContext() as any;
  const [searchParams, setSearchParams] = useSearchParams();
  const { me } = useMe();
  const currentOrganization = useCurrentOrganization();
  const [imageSearchScreensResults, setImageSearchScreenResults] = useState([]);
  const [imageSearchScreenResultsNext, setImageSearchScreenResultsNext] = useState(true);
  const [imageSearchScreenResultsLoading, setImageSearchScreenResultsLoading] =
    useState(false);
  const [imageSearchScreenResultsPage, setImageSearchScreenResultsPage] = useState(1);
  const [requestsMade, setRequestsMade] = useState(0);
  const isOCRSearch = searchParams.get("smode") === SEARCH_MODE_OCR;
  const [selectedItem, setSelectedItem] = useState();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState();
  const [currentAppIndex, setCurrentAppIndex] = useState<number | null>(null);
  const [currentScreenNotBlurredID, setCurrentScreenNotBlurredID] = useState<
    number | null
  >(null);
  const [appDataToShow, setAppDataToShow] = useState<any>(null);
  const [transitionDirection, setTransitionDirection] = useState<
    "next" | "prev" | null
  >(null);

  const loaderRef = useRef(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchImageSearchScreenResults = (startIndex: number, stopIndex: number) => {
    setImageSearchScreenResultsLoading(true);

    const page = 1;
    setImageSearchScreenResultsPage(page);
    setImageSearchScreenResultsLoading(true);
    setImageSearchScreenResults([]);

    const p = {
      image: id,
      page: page,
      page_size: 10,
      ...filterParams,
    };
    api
      .get("/v1/image-search/", { params: p })
      .then((res) => {
        setImageSearchScreenResultsNext(res.data.next);
        setImageSearchScreenResults(res.data.results);

        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      })
      .catch((error) => {
        console.error("Search failed:", error);
        toast.error("Search failed");
      })
      .finally(() => {
        setRequestsMade(requestsMade + 1);
        setImageSearchScreenResultsLoading(false);
      });
  };

  const fetchNextPage = useCallback(() => {
    if (!imageSearchScreenResultsNext || isLoadingMore) return;

    setIsLoadingMore(true);
    const nextPage = imageSearchScreenResultsPage + 1;

    const p = {
      image: id,
      page: nextPage,
      page_size: 10,
      ...filterParams,
    };

    api
      .get("/v1/image-search/", { params: p })
      .then((res) => {
        setImageSearchScreenResultsNext(res.data.next);
        setImageSearchScreenResults((prev) => [...prev, ...res.data.results]);
        setImageSearchScreenResultsPage(nextPage);
      })
      .catch((error) => {
        console.error("Failed to load more results:", error);
        toast.error("Failed to load more results");
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [
    imageSearchScreenResultsNext,
    imageSearchScreenResultsPage,
    id,
    filterParams,
    isLoadingMore,
    api,
  ]);

  // Intersection Observer for infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && imageSearchScreenResultsNext && !isLoadingMore) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    const currentLoaderRef = loaderRef.current;
    if (currentLoaderRef) {
      observer.observe(currentLoaderRef);
    }

    return () => {
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
    };
  }, [fetchNextPage, imageSearchScreenResultsNext, isLoadingMore]);

  useEffect(() => {
    fetchImageSearchScreenResults();
  }, [filterParams, id]);

  const handleExpandClick = (screen: any, index: number) => {
    setSelectedItem(screen);
    setSelectedImage(screen.screen);
    setCurrentImageIndex(index);
  };

  const handleNextImage = () => {
    if (!imageSearchScreensResults.length) {
      return;
    }

    const nextIndex = (currentImageIndex + 1) % imageSearchScreensResults.length;

    const nextResult = imageSearchScreensResults[nextIndex];
    if (nextResult) {
      setSelectedItem(nextResult);
      setSelectedImage(nextResult.screen);
      setCurrentImageIndex(nextIndex);
    }
  };

  const handlePrevImage = () => {
    if (!imageSearchScreensResults.length) {
      return;
    }

    const prevIndex =
      (currentImageIndex - 1 + imageSearchScreensResults.length) %
      imageSearchScreensResults.length;

    const prevResult = imageSearchScreensResults[prevIndex];
    if (prevResult) {
      setSelectedItem(prevResult);
      setSelectedImage(prevResult.screen);
      setCurrentImageIndex(prevIndex);
    }
  };

  const handleAppClick = (app, timestamp: number, appvideoId: number) => {
    handleOpenVideoPage(
      timestamp,
      app.slug,
      "Image search",
      1,
      app.id,
      appvideoId,
      undefined,
      me?.username || "",
      currentOrganization?.uuid || "",
    );
  };

  const handleRefButton = () => {
    dispatch(filterActions.activateFilterMagic());
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (selectedImage) {
        if (event.key === "ArrowRight") {
          handleNextImage();
        } else if (event.key === "ArrowLeft") {
          handlePrevImage();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImage, handleNextImage, handlePrevImage]);

  useEffect(() => {
    if (imageSearchScreensResults.length && selectedItem) {
      setSelectedItem(
        imageSearchScreensResults.find((item) => item.id == selectedItem.id),
      );
    }
  }, [imageSearchScreensResults]);

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    imageId: string;
    timestamp: string;
    app_video_id: number;
  } | null>(null);

  const handleContextMenu = (
    event: React.MouseEvent,
    imageId: string,
    timestamp: number,
    app_video_id: number,
  ) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      imageId,
      timestamp: timestamp.toString(),
      app_video_id,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleNextApp = (currentIndex: number) => {
    const nextIndex = currentIndex + 1;
    const nextResult = imageSearchScreensResults[nextIndex];
    setCurrentAppIndex(nextIndex);
    setTransitionDirection("next");
    setAppDataToShow(nextResult.app || null);
  };

  const handlePreviousApp = (currentIndex: number) => {
    const previousIndex = currentIndex - 1;
    const previousResult = imageSearchScreensResults[previousIndex];
    setCurrentAppIndex(previousIndex);
    setTransitionDirection("prev");
    setAppDataToShow(previousResult.app || null);
  };

  const { selectedScreens } = useSelectedScreens();

  useEffect(() => {
    // Sync the loading state with Redux
    dispatch(
      loadingStatesActions.setImageSearchLoading(
        imageSearchScreenResultsLoading || isLoadingMore,
      ),
    );
  }, [imageSearchScreenResultsLoading, isLoadingMore, dispatch]);

  return (
    <div className={`${isActiveMagic ? styles.magicOcrOverlay : ""}`}>
      {!imageSearchScreensResults.length &&
        !imageSearchScreenResultsLoading &&
        requestsMade > 0 && (
          <div className={styles.center}>
            <div className={styles.noMatches}>
              <img
                src="/img/Illustrations.jpg"
                width="250px"
                height="250px"
                alt="no results"
              />
              <h2>No Image Search Results Found</h2>
              <p>It looks like there are no matching results for your image.</p>

              <button onClick={handleRefButton} className={styles.button}>
                {isOCRSearch
                  ? "Try using smart AI"
                  : "Try searching for something new"}
              </button>
            </div>
          </div>
        )}

      <div className={styles.similarScreenContainer}>
        <div className={styles.similarScreenModalContainer}>
          <SimilarScreenModal type="search" />
        </div>
        <div className={styles.noContextContainerWrapper}>
          <div className={styles.noContextContainer}>
            {imageSearchScreensResults.map((item, index) => (
              <NoContextItems
                key={`${item.id}-${index}`}
                index={index}
                app={item}
                setSearchResults={setImageSearchScreenResults}
                onAppClick={handleAppClick}
                handleExpandClick={handleExpandClick}
                handleOpenBillingPage={handleOpenBillingPage}
                setSearchValue={setSearchValue}
                handleContextMenu={handleContextMenu}
                currentAppIndex={currentAppIndex}
                appIndex={index}
                setCurrentAppIndex={setCurrentAppIndex}
                onNextApp={handleNextApp}
                onPreviousApp={handlePreviousApp}
                appDataToShow={appDataToShow}
                transitionDirection={transitionDirection}
                searchResults={imageSearchScreensResults}
                currentScreenNotBlurredID={currentScreenNotBlurredID}
                setCurrentScreenNotBlurredID={setCurrentScreenNotBlurredID}
              />
            ))}

            {/* Loader reference element */}
            {imageSearchScreenResultsNext && !imageSearchScreenResultsLoading && (
              <div ref={loaderRef} className={styles.loaderContainer} />
            )}

            {contextMenu && (
              <RightClickModal
                x={contextMenu.x}
                y={contextMenu.y}
                onClose={handleCloseContextMenu}
                imageUrl={
                  imageSearchScreensResults.find(
                    (item) => item.id.toString() === contextMenu.imageId,
                  )?.screen || ""
                }
                imageId={contextMenu.imageId || ""}
                handleOpenInNewTab={() => {
                  const resultItem = imageSearchScreensResults.find(
                    (item) => item.id.toString() === contextMenu.imageId,
                  );
                  if (resultItem) {
                    handleAppClick(
                      resultItem.app,
                      Number(contextMenu.timestamp),
                      resultItem.app_video_id,
                    );
                  }
                }}
                timestamp={Number(contextMenu.timestamp)}
                imageType="video"
                refetchData={() => {
                  const currentItem = imageSearchScreensResults.find(
                    (item) => item.id.toString() === contextMenu.imageId,
                  );
                  if (currentItem) {
                    setImageSearchScreenResults(
                      produce((draft) => {
                        const index = draft.findIndex(
                          (obj) => obj.id.toString() === contextMenu.imageId,
                        );
                        if (index !== -1) {
                          draft[index] = {
                            ...draft[index],
                            is_saved: !draft[index].is_saved,
                          };
                        }
                      }),
                    );
                  }
                }}
                avsID={contextMenu.app_video_id}
                onlyCopy
              />
            )}
          </div>
        </div>
      </div>

      {selectedScreens.length > 0 && (
        <SelectedScreensPopup
          type="appReplays"
          setSimilarScreenResults={setImageSearchScreenResults}
        />
      )}
    </div>
  );
};

export default ImageSearch;
